import { z } from 'zod';
import {
  EVENT_ATTENDANCE_STATUS_VALUES,
  EVENT_CHECK_IN_MODE_VALUES,
  INSTITUTIONAL_EVENT_STATUS_VALUES,
  INSTITUTIONAL_EVENT_TYPE_VALUES,
} from '@fas/types';
import { GPS_FAILURE_VALUES, gpsReadingSchema, scanEvidenceSchema } from './attendance';
import { booleanQuerySchema, isoDateSchema, nonEmptyTrimmed, optionalText, paginationQuerySchema, timeOfDaySchema, uuidSchema } from './common';
import { deviceProofSchema } from './devices';

// ── Institutional events (§8.1, §67, Phase 2E, ADR-048) ────────────────────────
/** Who is invited. Entries add up; the same faculty member is invited once. */
export const eventAudienceEntrySchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('INSTITUTION') }).strict(),
  z.object({ scope: z.literal('CAMPUS'), campusId: uuidSchema }).strict(),
  z.object({ scope: z.literal('COLLEGE'), collegeId: uuidSchema }).strict(),
  z.object({ scope: z.literal('DEPARTMENT'), departmentId: uuidSchema }).strict(),
  z.object({ scope: z.literal('EMPLOYMENT_TYPE'), employmentTypeCode: z.string().trim().toUpperCase().min(1).max(30) }).strict(),
  z.object({ scope: z.literal('FACULTY'), facultyId: uuidSchema }).strict(),
]);
export type EventAudienceEntryInput = z.infer<typeof eventAudienceEntrySchema>;

const audienceKey = (a: EventAudienceEntryInput) => JSON.stringify(a);

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .pipe(z.url({ protocol: /^https$/, error: 'Must be an https:// link' }));

export const EVENT_MODALITY_VALUES = ['FACE_TO_FACE', 'ONLINE_SYNCHRONOUS', 'HYBRID'] as const;

const eventShape = {
  title: nonEmptyTrimmed(200, 'Title'),
  description: optionalText(2000),
  eventType: z.enum(INSTITUTIONAL_EVENT_TYPE_VALUES),
  eventDate: isoDateSchema,
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  /** A room, hall or campus from the locations list; `venue` describes other places. */
  locationId: uuidSchema.nullable().optional(),
  venue: optionalText(200),
  modality: z.enum(EVENT_MODALITY_VALUES),
  meetingUrl: httpsUrl.nullable().optional(),
  /** Required events close with ABSENT or EXCUSED for those who did not check in. */
  attendanceRequired: z.boolean(),
  /** Faculty outside the audience may check in with the event's code. */
  allowWalkIns: z.boolean(),
  checkInMode: z.enum(EVENT_CHECK_IN_MODE_VALUES),
  /** Check-in opens this many minutes before the start and stays open until the end. */
  checkInOpensMinutes: z.number().int().min(0).max(240),
  /** Check-ins after the start plus this many minutes are LATE. */
  lateAfterMinutes: z.number().int().min(0).max(240),
  /** Unit that owns the event (null for institution-wide); decides who may manage it. */
  collegeId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  audience: z.array(eventAudienceEntrySchema).min(1, 'Choose who is invited').max(50),
};

function refineEvent(
  v: { startTime?: string; endTime?: string; collegeId?: string | null; departmentId?: string | null; audience?: EventAudienceEntryInput[]; modality?: string; meetingUrl?: string | null },
  ctx: z.RefinementCtx,
) {
  if (v.startTime && v.endTime && v.endTime <= v.startTime) ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'End time must be after the start time' });
  if (v.collegeId && v.departmentId) ctx.addIssue({ code: 'custom', path: ['departmentId'], message: 'Choose either a college or a department, not both' });
  if (v.audience && new Set(v.audience.map(audienceKey)).size !== v.audience.length) {
    ctx.addIssue({ code: 'custom', path: ['audience'], message: 'Each audience entry may appear once' });
  }
  if (v.modality === 'FACE_TO_FACE' && v.meetingUrl) ctx.addIssue({ code: 'custom', path: ['meetingUrl'], message: 'A meeting link is for online or hybrid events' });
}

export const createEventSchema = z
  .object({
    ...eventShape,
    modality: eventShape.modality.default('FACE_TO_FACE'),
    attendanceRequired: eventShape.attendanceRequired.default(true),
    allowWalkIns: eventShape.allowWalkIns.default(false),
    checkInMode: eventShape.checkInMode.default('QR_OR_APP'),
    checkInOpensMinutes: eventShape.checkInOpensMinutes.default(30),
    lateAfterMinutes: eventShape.lateAfterMinutes.default(10),
  })
  .strict()
  .superRefine(refineEvent);
export type CreateEventInput = z.infer<typeof createEventSchema>;

/**
 * Drafts can change everything; published events change their time, place and audience only
 * before they start (attendees are told), and afterwards only their description.
 */
export const updateEventSchema = z.object(eventShape).partial().strict().superRefine(refineEvent);
export type UpdateEventInput = z.infer<typeof updateEventSchema>;

export const cancelEventSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type CancelEventInput = z.infer<typeof cancelEventSchema>;

export const listEventsQuerySchema = paginationQuerySchema.extend({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  status: z.enum(INSTITUTIONAL_EVENT_STATUS_VALUES).optional(),
  eventType: z.enum(INSTITUTIONAL_EVENT_TYPE_VALUES).optional(),
  collegeId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});
export type ListEventsQuery = z.infer<typeof listEventsQuerySchema>;

export const myEventsQuerySchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Only events whose check-in is open now. */
  openNow: booleanQuerySchema.optional(),
});
export type MyEventsQuery = z.infer<typeof myEventsQuerySchema>;

export const eventCheckInSchema = z
  .object({
    /** Idempotency key: repeating a check-in returns the first one. */
    clientRequestId: uuidSchema,
    /** A scan of the event's code or of a display at the venue. */
    scan: scanEvidenceSchema.optional(),
    location: gpsReadingSchema.nullable().optional(),
    gpsError: z.enum(GPS_FAILURE_VALUES).nullable().optional(),
    device: deviceProofSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.location && v.gpsError) ctx.addIssue({ code: 'custom', path: ['gpsError'], message: 'Send either a location or a GPS error, not both' });
  });
export type EventCheckInInput = z.infer<typeof eventCheckInSchema>;

export const markEventAttendeeSchema = z
  .object({
    status: z.enum(EVENT_ATTENDANCE_STATUS_VALUES).exclude(['EXPECTED']),
    reason: nonEmptyTrimmed(500, 'Reason'),
  })
  .strict();
export type MarkEventAttendeeInput = z.infer<typeof markEventAttendeeSchema>;

export const addEventAttendeeSchema = z.object({ facultyId: uuidSchema, reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type AddEventAttendeeInput = z.infer<typeof addEventAttendeeSchema>;

export const listEventAttendeesQuerySchema = paginationQuerySchema.extend({
  status: z.enum(EVENT_ATTENDANCE_STATUS_VALUES).optional(),
  departmentId: uuidSchema.optional(),
  /** Only check-ins with flags that nobody has reviewed yet. */
  needsReview: booleanQuerySchema.optional(),
});
export type ListEventAttendeesQuery = z.infer<typeof listEventAttendeesQuerySchema>;

export const eventsReportQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    collegeId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.to < v.from) ctx.addIssue({ code: 'custom', path: ['to'], message: 'Must be on or after "from"' });
  });
export type EventsReportQuery = z.infer<typeof eventsReportQuerySchema>;
